// API client for the "Revenue Plays" commercial model.
// Backend contract: shopify-etl-upsellerate/billing/plays_routes.py +
// plays_model.py. This is the ONLY source of truth for pricing math and
// client-facing copy; the frontend never re-derives entitlement numbers,
// it renders exactly what the backend returns (meter string, launch button
// copy, over_capacity, error codes, etc.).

const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

export const PLAYS_API_URL =
  process.env.REACT_APP_BILLING_API_URL ||
  (isLocalhost ? 'http://localhost:8080' : 'https://mi-chat-892931479853.us-central1.run.app');

async function parseJsonSafe(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

class PlaysApiError extends Error {
  constructor(message, { code, meta, status } = {}) {
    super(message);
    this.code = code || null;
    this.meta = meta || null;
    this.status = status || null;
  }
}

async function playsFetch(path, { token, method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(`${PLAYS_API_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await parseJsonSafe(response);

  if (!response.ok) {
    const message = data?.error || data?.detail || data?.message || `Request failed (${response.status})`;
    throw new PlaysApiError(message, { code: data?.code, meta: data?.meta, status: response.status });
  }

  return data;
}

// GET /billing/plans (public, no auth) -> { plans, currency, pack_price_monthly,
// counting_rule, founding_disclosure }
export function fetchPublicPlans() {
  return playsFetch('/billing/plans');
}

// GET /billing/entitlements[?refresh=1] -> full entitlements + meter payload,
// see plays_routes._entitlements_payload for the exact shape.
export function fetchEntitlements({ token, refresh = false } = {}) {
  return playsFetch(`/billing/entitlements${refresh ? '?refresh=1' : ''}`, { token });
}

// POST /billing/select-plan {plan, play_packs} -> {confirmationUrl, subscription_id, plan, play_packs}
export function selectPlan({ token, plan, playPacks = 0 }) {
  return playsFetch('/billing/select-plan', {
    token,
    method: 'POST',
    body: { plan, play_packs: playPacks },
  });
}

// POST /billing/packs {play_packs} -> {confirmationUrl, subscription_id, plan, play_packs}
export function changePacks({ token, playPacks }) {
  return playsFetch('/billing/packs', {
    token,
    method: 'POST',
    body: { play_packs: playPacks },
  });
}

// POST /billing/cancel -> {message, ...entitlements}
export function cancelSubscription({ token }) {
  return playsFetch('/billing/cancel', { token, method: 'POST' });
}

// POST /plays {name, objective, type, audience_ref, origin, opportunity_id} -> {play, note}
export function createDraftPlay({ token, name, objective, type, audienceRef, origin, opportunityId }) {
  return playsFetch('/plays', {
    token,
    method: 'POST',
    body: {
      name,
      objective,
      type,
      audience_ref: audienceRef,
      origin,
      opportunity_id: opportunityId,
    },
  });
}

// GET /plays -> {plays, meter, over_capacity}
export function fetchPlays({ token }) {
  return playsFetch('/plays', { token });
}

// POST /plays/:id/activate {mode: 'launch_once' | 'keep_updated', audience_size}
// -> {play, ...entitlements} or throws PlaysApiError with .code in
// {play_not_found, free_activation_blocked, one_time_limit_reached, always_on_limit_reached}
export function activatePlay({ token, playId, mode, deliveryType, provider, audienceSize }) {
  return playsFetch(`/plays/${playId}/activate`, {
    token,
    method: 'POST',
    body: { mode, delivery_type: deliveryType, provider, audience_size: audienceSize },
  });
}

// POST /plays/activate-segment {segment, mode}
// -> {play, segment_slug, play_key, delivery, ...entitlements}
// We intentionally do NOT send activation_channel/provider: the merchant's
// connected email platform (Klaviyo, Omnisend, ...) is resolved server-side
// from ServiceAuth. Hardcoding 'klaviyo' here gave Omnisend merchants a
// misleading "connect Klaviyo" 409. Pass activationChannel only for an
// explicit override (kept optional for callers that know the provider).
// Throws PlaysApiError with .code in {segment_not_activatable,
// free_activation_blocked, one_time_limit_reached, always_on_limit_reached,
// delivery_provider_not_ready} and .status (400/402/403/409).
export function activateSegmentPlay({ token, segment, mode = 'keep_updated', activationChannel }) {
  const body = { segment, mode };
  if (activationChannel) body.activation_channel = activationChannel;
  return playsFetch('/plays/activate-segment', { token, method: 'POST', body });
}

// POST /plays/:id/pause -> {play, ...entitlements}
export function pausePlay({ token, playId }) {
  return playsFetch(`/plays/${playId}/pause`, { token, method: 'POST' });
}

// POST /plays/:id/archive -> {play, ...entitlements}
export function archivePlay({ token, playId }) {
  return playsFetch(`/plays/${playId}/archive`, { token, method: 'POST' });
}

// POST /stores/connect-second {second_shop} -> 409 {blocked, message, contact}
export function requestSecondStore({ token, secondShop }) {
  return playsFetch('/stores/connect-second', {
    token,
    method: 'POST',
    body: { second_shop: secondShop },
  });
}

export { PlaysApiError };
